import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLists } from '../contexts/ListsContext';

import { subscribeToList, subscribeToListWishes, addWish, updateWish, deleteWish, markGiftAsPlanned, markGiftAsBought, unmarkGift, deleteList, joinSharedList, clearErrorLogs } from '../firebase/database';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ArrowLeft, Plus, Edit2, Trash2, ShoppingCart, CheckCircle, Heart, Share2, ChevronDown, ChevronUp, Gift, User, Users, Grid, List, LayoutGrid, Flame, Star, Leaf } from 'lucide-react';
import { formatCurrency } from '../services/currencyService';
import { getStoreName, formatUrl } from '../services/productScraper';
import { getCategoryIcon } from '../services/categoryService';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import WishForm from '../components/WishForm';
import ShareListModal from '../components/ShareListModal';
import ConfirmModal from '../components/ConfirmModal';
import ListMetaTags from '../components/ListMetaTags';
import { useTranslation } from '../hooks/useTranslation';

// Global flags to prevent duplicate toasts across component instances
const joinedLists = new Set();
const errorToastsShown = new Set();


const ListDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { forceRefresh } = useLists();
  const { t } = useTranslation();

  // Function to extract domain from URL
  const getDomainFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch (error) {
      console.error('Error parsing URL:', error);
      return 'website';
    }
  };

  // Function to separate wishes into my wishes and others' wishes
  const separateWishes = (wishes) => {
    const myWishes = wishes.filter(wish => wish.createdBy === user.uid);
    const othersWishes = wishes.filter(wish => wish.createdBy !== user.uid);
    
    return {
      myWishes,
      othersWishes
    };
  };

  // Function to group others' wishes by user
  const groupWishesByUser = (wishes) => {
    const grouped = {};
    wishes.forEach(wish => {
      const userId = wish.createdBy;
      if (!grouped[userId]) {
        grouped[userId] = [];
      }
      grouped[userId].push(wish);
    });
    return grouped;
  };

  // Toggle collapse state for a specific user
  const toggleUserCollapse = (userId) => {
    setCollapsedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Component to render a wish
  const renderWish = (wish) => {
    // Determine background color and border based on status
    let statusBgClass = 'bg-amber-50';
    let borderClass = 'border border-gray-200';
    
    // Only show status colors to non-creators (privacy feature)
    if (wish.createdBy !== user.uid) {
      // Check if arrays exist and have items
      const boughtByArray = wish.boughtBy || [];
      const plannedByArray = wish.plannedBy || [];
      const isBought = Array.isArray(boughtByArray) && boughtByArray.length > 0;
      const isPlanned = Array.isArray(plannedByArray) && plannedByArray.length > 0;
      
      if (isBought && isPlanned) {
        // Both planned and bought - light red
        statusBgClass = 'bg-red-100/90';
        borderClass = 'border border-red-200';
      } else if (isBought) {
        // Only bought - green
        statusBgClass = 'bg-green-100/90';
        borderClass = 'border border-green-200';
      } else if (isPlanned) {
        // Only planned - blue
        statusBgClass = 'bg-blue-100/90';
        borderClass = 'border border-blue-200';
      }
    }
    
    // List mode keeps the spacious horizontal layout
    if (gridLayout === 'list') {
      return (
        <div key={wish.id} className={`${statusBgClass} ${borderClass} rounded-lg shadow-sm p-4 sm:p-6 transition-all duration-200 hover:shadow-md`}>
          {/* Creator name and action buttons at the top */}
          <div className="flex items-center justify-between text-sm text-gray-600 mb-3 pb-2 border-b border-gray-100">
            {wish.createdBy && (
              <div className="flex items-center min-w-0 flex-1">
                <User className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="font-medium truncate">{wish.createdBy === user.uid ? (user?.displayName || user?.email?.split('@')[0] || t('common.unknownUser')) : (memberNames[wish.createdBy] || t('common.unknownUser'))}</span>
              </div>
            )}
            
            {/* Edit and Delete buttons for wish creator */}
            {isMember && wish.createdBy === user.uid && (
              <div className="flex space-x-1 ml-2">
                <button
                  onClick={() => setEditingWish(wish)}
                  className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors touch-target"
                  title={t('listDetail.editWish')}
                >
                  <Edit2 className="w-4 h-4" />
                </button>

                <button
                  onClick={() => handleDeleteWish(wish.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors touch-target"
                  title={t('listDetail.deleteWish')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          
          {/* Product Image or Category Icon */}
          {wish.imageUrl && !wish.imageUrl.includes('placeholder') ? (
            <div className="mb-3 w-20 h-20">
              <img 
                src={wish.imageUrl} 
                alt={wish.name}
                className="w-full h-full object-cover rounded-lg border border-gray-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          ) : wish.category ? (
            <div className="mb-3 flex items-center justify-center w-20 h-20 bg-primary-50 rounded-lg border border-primary-100">
              {(() => {
                const CategoryIcon = getCategoryIcon(wish.category);
                return <CategoryIcon className="text-primary-600" size={40} />;
              })()}
            </div>
          ) : (
            <div className="mb-3 flex items-center justify-center w-20 h-20 bg-gray-100 rounded-lg border border-gray-200">
              <Gift className="text-gray-400" size={40} />
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 mb-2 text-lg sm:text-xl leading-tight">{wish.name}</h3>
              {wish.description && (
                <p className="text-gray-600 mb-3 text-sm sm:text-base leading-relaxed">{wish.description}</p>
              )}
              
              <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-3">
                {wish.price && (
                  <span className="font-medium text-green-600">
                    {formatCurrency(wish.price, wish.currency || list.defaultCurrency)}
                  </span>
                )}
                {wish.priority && (
                  <span className="flex items-center">
                    <span className="mr-1">
                      {wish.priority === 'high' && <Flame className="w-4 h-4 text-red-500" />}
                      {wish.priority === 'medium' && <Star className="w-4 h-4 text-yellow-500" />}
                      {wish.priority === 'low' && <Leaf className="w-4 h-4 text-green-500" />}
                    </span>
                    <span>
                      {wish.priority === 'high' && t('wishForm.priorityHigh')}
                      {wish.priority === 'medium' && t('wishForm.priorityMedium')}
                      {wish.priority === 'low' && t('wishForm.priorityLow')}
                    </span>
                  </span>
                )}
                {wish.store && (
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    {getStoreName(wish.store)}
                  </span>
                )}
                {wish.url && (
                  <a
                    href={formatUrl(wish.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {t('listDetail.viewProductOn')} {getDomainFromUrl(wish.url)}
                  </a>
                )}
              </div>

              {/* Only show buyer/planner info to non-creators to maintain privacy */}
              {wish.createdBy !== user.uid && wish.plannedBy && wish.plannedBy.length > 0 && (
                <div className="flex items-center text-xs sm:text-sm text-blue-600 mb-2">
                  <ShoppingCart className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                  <span className="truncate">{t('listDetail.plannedBy')} {wish.plannedBy.map(userId => memberNames[userId] || t('common.unknownUser')).join(', ')}</span>
                </div>
              )}

              {wish.createdBy !== user.uid && wish.boughtBy && wish.boughtBy.length > 0 && (
                <div className="flex items-center text-xs sm:text-sm text-green-600">
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                  <span className="truncate">{t('listDetail.boughtBy')} {wish.boughtBy.map(userId => memberNames[userId] || t('common.unknownUser')).join(', ')}</span>
                </div>
              )}

            </div>

            {isMember && (
              <div className="flex flex-col sm:flex-row sm:space-x-2 sm:space-y-0 mt-3 sm:mt-0 sm:ml-4">
                {wish.createdBy !== user.uid && (
                  <>
                    <button
                      onClick={() => handleMarkAsPlanned(wish.id)}
                      className={`flex items-center justify-center px-4 py-3 rounded-lg transition-colors touch-target text-sm font-medium ${
                        wish.plannedBy && wish.plannedBy.includes(user.uid)
                          ? 'bg-blue-100 text-blue-700 border border-blue-300'
                          : 'text-blue-600 hover:bg-blue-50 border border-blue-200'
                      }`}
                      title={wish.plannedBy && wish.plannedBy.includes(user.uid) ? t('listDetail.unmarkPlanned') : t('listDetail.markAsPlanned')}
                    >
                      <ShoppingCart className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">
                        {wish.plannedBy && wish.plannedBy.includes(user.uid) ? t('listDetail.unmarkPlanned') : t('listDetail.markAsPlanned')}
                      </span>
                    </button>
                    
                    <button
                      onClick={() => handleMarkAsBought(wish.id)}
                      className={`flex items-center justify-center px-4 py-3 rounded-lg transition-colors touch-target text-sm font-medium ${
                        wish.boughtBy && wish.boughtBy.includes(user.uid)
                          ? 'bg-green-100 text-green-700 border border-green-300'
                          : 'text-green-600 hover:bg-green-50 border border-green-200'
                      }`}
                      title={wish.boughtBy && wish.boughtBy.includes(user.uid) ? t('listDetail.unmarkBought') : t('listDetail.markAsBought')}
                    >
                      <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">
                        {wish.boughtBy && wish.boughtBy.includes(user.uid) ? t('listDetail.unmarkBought') : t('listDetail.markAsBought')}
                      </span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Grid and Two-column mode: structured vertical card with bottom action buttons
    const isGrid = gridLayout === 'grid';
    const isTwoColumn = gridLayout === 'two-column';

    return (
      <div
        key={wish.id}
        className={`${statusBgClass} ${borderClass} rounded-xl shadow-sm ${
          isGrid ? 'p-3.5 sm:p-4' : 'p-4 sm:p-5'
        } transition-all duration-200 hover:shadow-md flex flex-col justify-between h-full`}
      >
        <div className="flex-1 flex flex-col min-w-0">
          {/* Creator name and action buttons at the top */}
          <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 mb-3 pb-2 border-b border-gray-100">
            {wish.createdBy && (
              <div className="flex items-center min-w-0 flex-1 mr-2">
                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 flex-shrink-0" />
                <span className="font-medium truncate">
                  {wish.createdBy === user.uid
                    ? (user?.displayName || user?.email?.split('@')[0] || t('common.unknownUser'))
                    : (memberNames[wish.createdBy] || t('common.unknownUser'))}
                </span>
              </div>
            )}

            {/* Edit and Delete buttons for wish creator */}
            {isMember && wish.createdBy === user.uid && (
              <div className="flex space-x-1 flex-shrink-0">
                <button
                  onClick={() => setEditingWish(wish)}
                  className="p-1.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors touch-target"
                  title={t('listDetail.editWish')}
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>

                <button
                  onClick={() => handleDeleteWish(wish.id)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors touch-target"
                  title={t('listDetail.deleteWish')}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Product Image & Title Row */}
          <div className="flex items-start gap-3 mb-3">
            {/* Product Image or Category Icon */}
            {wish.imageUrl && !wish.imageUrl.includes('placeholder') ? (
              <div className={`${isGrid ? 'w-14 h-14 sm:w-16 sm:h-16' : 'w-16 h-16 sm:w-20 sm:h-20'} flex-shrink-0`}>
                <img 
                  src={wish.imageUrl} 
                  alt={wish.name}
                  className="w-full h-full object-cover rounded-lg border border-gray-200"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            ) : wish.category ? (
              <div className={`${isGrid ? 'w-14 h-14 sm:w-16 sm:h-16' : 'w-16 h-16 sm:w-20 sm:h-20'} flex-shrink-0 flex items-center justify-center bg-primary-50 rounded-lg border border-primary-100`}>
                {(() => {
                  const CategoryIcon = getCategoryIcon(wish.category);
                  return <CategoryIcon className="text-primary-600" size={isGrid ? 28 : 36} />;
                })()}
              </div>
            ) : (
              <div className={`${isGrid ? 'w-14 h-14 sm:w-16 sm:h-16' : 'w-16 h-16 sm:w-20 sm:h-20'} flex-shrink-0 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200`}>
                <Gift className="text-gray-400" size={isGrid ? 28 : 36} />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <h3 className={`font-semibold text-gray-900 ${isGrid ? 'text-sm sm:text-base' : 'text-base sm:text-lg'} leading-snug line-clamp-2`}>
                {wish.name}
              </h3>
              {wish.price && (
                <p className={`font-semibold text-green-600 ${isGrid ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'} mt-1`}>
                  {formatCurrency(wish.price, wish.currency || list.defaultCurrency)}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          {wish.description && (
            <p className={`text-gray-600 mb-3 ${isGrid ? 'text-xs sm:text-sm line-clamp-2' : 'text-sm line-clamp-3'} leading-relaxed`}>
              {wish.description}
            </p>
          )}

          {/* Meta Badges */}
          <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-3">
            {wish.priority && (
              <span className="inline-flex items-center">
                <span className="mr-1">
                  {wish.priority === 'high' && <Flame className="w-3.5 h-3.5 text-red-500" />}
                  {wish.priority === 'medium' && <Star className="w-3.5 h-3.5 text-yellow-500" />}
                  {wish.priority === 'low' && <Leaf className="w-3.5 h-3.5 text-green-500" />}
                </span>
                <span>
                  {wish.priority === 'high' && t('wishForm.priorityHigh')}
                  {wish.priority === 'medium' && t('wishForm.priorityMedium')}
                  {wish.priority === 'low' && t('wishForm.priorityLow')}
                </span>
              </span>
            )}
            {wish.store && (
              <span className="inline-flex items-center">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-1.5"></span>
                {getStoreName(wish.store)}
              </span>
            )}
            {wish.url && (
              <a
                href={formatUrl(wish.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 transition-colors inline-flex items-center truncate max-w-full"
              >
                {t('listDetail.viewProductOn')} {getDomainFromUrl(wish.url)}
              </a>
            )}
          </div>

          {/* Only show buyer/planner info to non-creators to maintain privacy */}
          {wish.createdBy !== user.uid && wish.plannedBy && wish.plannedBy.length > 0 && (
            <div className="flex items-center text-xs text-blue-600 mb-1.5">
              <ShoppingCart className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
              <span className="truncate">
                {t('listDetail.plannedBy')} {wish.plannedBy.map(userId => memberNames[userId] || t('common.unknownUser')).join(', ')}
              </span>
            </div>
          )}

          {wish.createdBy !== user.uid && wish.boughtBy && wish.boughtBy.length > 0 && (
            <div className="flex items-center text-xs text-green-600 mb-1.5">
              <CheckCircle className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
              <span className="truncate">
                {t('listDetail.boughtBy')} {wish.boughtBy.map(userId => memberNames[userId] || t('common.unknownUser')).join(', ')}
              </span>
            </div>
          )}
        </div>

        {/* Action Buttons for non-creators */}
        {isMember && wish.createdBy !== user.uid && (
          <div className={`mt-auto pt-3 border-t border-gray-100/80 ${
            isTwoColumn ? 'grid grid-cols-1 sm:grid-cols-2 gap-2' : 'flex flex-col space-y-2'
          }`}>
            {/* Plan to Buy Button */}
            <button
              onClick={() => handleMarkAsPlanned(wish.id)}
              className={`flex items-center justify-center w-full px-3 py-2 sm:py-2.5 rounded-lg transition-colors touch-target text-xs sm:text-sm font-medium ${
                wish.plannedBy && wish.plannedBy.includes(user.uid)
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'text-blue-600 hover:bg-blue-50 border border-blue-200'
              }`}
              title={wish.plannedBy && wish.plannedBy.includes(user.uid) ? t('listDetail.unmarkPlanned') : t('listDetail.markAsPlanned')}
            >
              <ShoppingCart className="w-4 h-4 mr-1.5 flex-shrink-0" />
              <span className="truncate">
                {wish.plannedBy && wish.plannedBy.includes(user.uid) ? t('listDetail.unmarkPlanned') : t('listDetail.markAsPlanned')}
              </span>
            </button>

            {/* Mark as Bought Button */}
            <button
              onClick={() => handleMarkAsBought(wish.id)}
              className={`flex items-center justify-center w-full px-3 py-2 sm:py-2.5 rounded-lg transition-colors touch-target text-xs sm:text-sm font-medium ${
                wish.boughtBy && wish.boughtBy.includes(user.uid)
                  ? 'bg-green-100 text-green-700 border border-green-300'
                  : 'text-green-600 hover:bg-green-50 border border-green-200'
              }`}
              title={wish.boughtBy && wish.boughtBy.includes(user.uid) ? t('listDetail.unmarkBought') : t('listDetail.markAsBought')}
            >
              <CheckCircle className="w-4 h-4 mr-1.5 flex-shrink-0" />
              <span className="truncate">
                {wish.boughtBy && wish.boughtBy.includes(user.uid) ? t('listDetail.unmarkBought') : t('listDetail.markAsBought')}
              </span>
            </button>
          </div>
        )}
      </div>
    );
  };

  const [list, setList] = useState(null);
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWishForm, setShowWishForm] = useState(false);
  const [editingWish, setEditingWish] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberNames, setMemberNames] = useState({});
  const [showMemberList, setShowMemberList] = useState(false);
  const [gridLayout, setGridLayout] = useState(() => {
    const saved = localStorage.getItem('wishList_gridLayout');
    return saved || 'list';
  }); // 'list', 'two-column', 'grid'
  const [showGridDropdown, setShowGridDropdown] = useState(false);
  const [myWishesCollapsed, setMyWishesCollapsed] = useState(false);
  const [othersWishesCollapsed, setOthersWishesCollapsed] = useState(false);
  const [collapsedUsers, setCollapsedUsers] = useState(new Set());
  const hasJoinedListRef = useRef(false);
  const isDeletingRef = useRef(false);
  const memberDropdownRef = useRef(null);
  const gridDropdownRef = useRef(null);

  const fetchMemberNames = useCallback(async (memberIds) => {
    try {
      const memberPromises = memberIds.map(async (memberId) => {
        // If this is the current user, use their auth data directly
        if (user && memberId === user.uid) {
          return { 
            id: memberId, 
            name: user.displayName || user.email || 'You' 
          };
        }
        
        // For other members, fetch from database
        try {
          const userDoc = await getDoc(doc(db, 'users', memberId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            return { 
              id: memberId, 
              name: userData.displayName || userData.email || t('common.unknownUser') 
            };
          } else {
            // User document doesn't exist, try to create it with basic info
            // We can't get the user's email/displayName from auth since we don't have access to other users' auth data
            // So we'll create a basic document and let them update it when they next log in
            try {
              await setDoc(doc(db, 'users', memberId), {
                uid: memberId,
                email: null, // Will be updated when user logs in
                displayName: null, // Will be updated when user logs in
                createdAt: new Date(),
                preferences: {
                  currency: 'NOK', // Default currency, will be updated when user logs in
                  location: null
                }
              });
              console.log(`Created missing user document for ${memberId}`);
              return { id: memberId, name: t('common.user') };
            } catch (createError) {
              console.error('Failed to create user document:', createError);
              return { id: memberId, name: t('common.unknownUser') };
            }
          }
        } catch (memberError) {
          // Silently handle permission errors for individual members
          return { id: memberId, name: t('common.unknownUser') };
        }
      });

      const members = await Promise.all(memberPromises);
      const memberNamesMap = {};
      members.forEach(member => {
        memberNamesMap[member.id] = member.name;
      });
      setMemberNames(memberNamesMap);
    } catch (error) {
      // Only log if it's not a permission error (which is expected when user leaves list)
      if (!error.message?.includes('permission-denied')) {
        console.error('ListDetail: Error fetching member names:', error);
      }
    }
  }, [user, t]);

  // Calculate wish counts directly in render
  const wishCounts = React.useMemo(() => {
    if (!wishes || wishes.length === 0) {
      return { total: 0, bought: 0, planned: 0, isComplete: false };
    }

    const totalWishes = wishes.length;
    const boughtWishes = wishes.filter(wish => wish.boughtBy && wish.boughtBy.length > 0).length;
    const plannedWishes = wishes.filter(wish => wish.plannedBy && wish.plannedBy.length > 0).length;
    const isComplete = totalWishes > 0 && boughtWishes === totalWishes;

    return {
      total: totalWishes,
      bought: boughtWishes,
      planned: plannedWishes,
      isComplete: isComplete
    };
  }, [wishes]);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target)) {
        setShowMemberList(false);
      }
      if (gridDropdownRef.current && !gridDropdownRef.current.contains(event.target)) {
        setShowGridDropdown(false);
      }
    };

    if (showMemberList || showGridDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMemberList, showGridDropdown]);

  // Save grid layout preference to localStorage
  useEffect(() => {
    localStorage.setItem('wishList_gridLayout', gridLayout);
  }, [gridLayout]);

  useEffect(() => {
    // Reset join state when list ID changes
    hasJoinedListRef.current = false;
    // Reset deletion state when list ID changes
    isDeletingRef.current = false;
    
    const setupListSubscription = async () => {
      if (!id) {
        navigate('/dashboard');
        return;
      }

      // Don't try to load list if user is not authenticated or if list is being deleted
      if (!user || isDeletingRef.current) {
        return;
      }

      try {
        // First, check if user needs to join the list before setting up subscription
        const listDoc = await getDoc(doc(db, 'lists', id));
        if (!listDoc.exists()) {
          navigate('/dashboard');
          return;
        }

        const listData = { id: listDoc.id, ...listDoc.data() };
        
        // If user is not a member of this list, join them first
        const joinKey = `${user.uid}-${id}`;
        if (user && listData.members && !listData.members.includes(user.uid) && !joinedLists.has(joinKey)) {
          console.log('Attempting to join shared list for user:', user.uid, 'list:', id);
          const joinResult = await joinSharedList(id, user.uid);
          if (joinResult.success) {
            console.log('Successfully joined list, showing toast');
            joinedLists.add(joinKey); // Mark as joined globally
            hasJoinedListRef.current = true; // Prevent duplicate joins
            toast.success(t('listDetail.addedToSharedList'));
            // Refresh the user's lists to show the newly joined list
            forceRefresh();
          } else {
            console.error('Failed to join shared list:', joinResult.error);
            toast.error(t('listDetail.failedToJoinList'));
            navigate('/dashboard');
            return;
          }
        }

        // Now set up the subscription with the user already as a member
        const unsubscribeList = subscribeToList(id, async (listData) => {
          if (!listData) {
            const errorKey = `list-not-found-${id}`;
            // Don't show error toast if we're in the process of deleting this list
            if (!errorToastsShown.has(errorKey) && !isDeletingRef.current) {
              errorToastsShown.add(errorKey);
              console.log('ListDetail: Would show error toast, but isDeleting:', isDeletingRef.current);
            }
            navigate('/dashboard');
            return;
          }

          setList(listData);

          // Refresh member names if it's a group list
          if (listData.isGroup && listData.members) {
            fetchMemberNames(listData.members);
          }

          setLoading(false);
        });

        // Subscribe to wishes for this list
        const unsubscribeWishes = await subscribeToListWishes(id, (wishesData) => {
          console.log('ListDetail: Wishes subscription callback received:', wishesData.length, 'wishes');
          setWishes(wishesData);
        });

        // Return cleanup function
        return () => {
          unsubscribeList();
          unsubscribeWishes();
        };
      } catch (error) {
        console.error('ListDetail: Error setting up subscriptions:', error);
        const errorKey = `list-load-error-${id}`;
        if (!errorToastsShown.has(errorKey)) {
          errorToastsShown.add(errorKey);
          toast.error(t('listDetail.errorLoadingList'));
        }
        setLoading(false);
      }
    };

    const cleanup = setupListSubscription();

    // Cleanup function to reset deletion state when component unmounts
    return () => {
      isDeletingRef.current = false;
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then(cleanupFn => cleanupFn && cleanupFn());
      } else if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  // Clean up global flags when user changes
  useEffect(() => {
    if (!user) {
      joinedLists.clear();
      errorToastsShown.clear();
      clearErrorLogs(); // Also clear database error logs
      
      // Clean up localStorage toast keys for the previous user
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('toast-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  }, [user]);

  const handleAddWish = async (wishData) => {
    try {
      console.log('ListDetail: Adding wish with data:', wishData);
      const result = await addWish(id, wishData, user.uid);
      console.log('ListDetail: Add wish result:', result);
      if (result.success) {
        toast.success(t('listDetail.wishAdded'));
        setShowWishForm(false);
        console.log('ListDetail: Wish added successfully, waiting for subscription update...');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('ListDetail: Error adding wish:', error);
      toast.error(t('listDetail.errorAddingWish'));
    }
  };

  const handleUpdateWish = async (wishId, wishData) => {
    try {
      const result = await updateWish(id, wishId, wishData);
      if (result.success) {
        toast.success(t('listDetail.wishUpdated'));
        setEditingWish(null);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('ListDetail: Error updating wish:', error);
      toast.error(t('listDetail.errorUpdatingWish'));
    }
  };

  const handleDeleteWish = async (wishId) => {
    try {
      const result = await deleteWish(id, wishId);
      if (result.success) {
        toast.success(t('listDetail.wishDeleted'));
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('ListDetail: Error deleting wish:', error);
      toast.error(t('listDetail.errorDeletingWish'));
    }
  };

  const handleDeleteList = async () => {
    isDeletingRef.current = true;
    try {
      const result = await deleteList(list.id);
      if (result.success) {
        toast.success(t('listDetail.listDeleted'));
        // Small delay to ensure the ref is set before navigation
        setTimeout(() => {
          navigate('/dashboard');
        }, 100);
      } else {
        toast.error(result.error);
        isDeletingRef.current = false; // Reset if deletion failed
      }
    } catch (error) {
      console.error('ListDetail: Error deleting list:', error);
      toast.error(t('listDetail.errorDeletingList'));
      isDeletingRef.current = false; // Reset if deletion failed
    }
    setShowDeleteModal(false);
  };

  const handleMarkAsPlanned = async (wishId) => {
    try {
      // Find the wish to check current status
      const wish = wishes.find(w => w.id === wishId);
      if (!wish) return;

      // If already planned, unmark it
      if (wish.plannedBy && wish.plannedBy.includes(user.uid)) {
        const result = await unmarkGift(id, wishId, user.uid);
        if (result.success) {
          toast.success(t('listDetail.giftUnmarked'));
        } else {
          toast.error(result.error);
        }
      } else {
        // If bought, first unmark bought, then mark as planned
        if (wish.boughtBy && wish.boughtBy.includes(user.uid)) {
          await unmarkGift(id, wishId, user.uid);
        }
        
        const result = await markGiftAsPlanned(id, wishId, user.uid);
        if (result.success) {
          toast.success(t('listDetail.giftMarkedAsPlanned'));
        } else {
          toast.error(result.error);
        }
      }
    } catch (error) {
      console.error('ListDetail: Error marking gift as planned:', error);
      toast.error(t('listDetail.errorMarkingGift'));
    }
  };

  const handleMarkAsBought = async (wishId) => {
    try {
      // Find the wish to check current status
      const wish = wishes.find(w => w.id === wishId);
      if (!wish) return;

      // If already bought, unmark it
      if (wish.boughtBy && wish.boughtBy.includes(user.uid)) {
        const result = await unmarkGift(id, wishId, user.uid);
        if (result.success) {
          toast.success(t('listDetail.giftUnmarked'));
        } else {
          toast.error(result.error);
        }
      } else {
        // If planned, first unmark planned, then mark as bought
        if (wish.plannedBy && wish.plannedBy.includes(user.uid)) {
          await unmarkGift(id, wishId, user.uid);
        }
        
        const result = await markGiftAsBought(id, wishId, user.uid);
        if (result.success) {
          toast.success(t('listDetail.giftMarkedAsBought'));
        } else {
          toast.error(result.error);
        }
      }
    } catch (error) {
      console.error('ListDetail: Error marking gift as bought:', error);
      toast.error(t('listDetail.errorMarkingGift'));
    }
  };

  const handleShareModalClose = useCallback(() => {
    setShowShareModal(false);
  }, []);

  // Close share modal when user logs out
  useEffect(() => {
    if (!user && showShareModal) {
      setShowShareModal(false);
    }
  }, [user, showShareModal]);

  if (!id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('listDetail.listNotFound')}</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('listDetail.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!list) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('listDetail.listNotFound')}</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('listDetail.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  const isOwner = user && list.createdBy === user.uid;
  const isMember = user && list.members && list.members.includes(user.uid);

  return (
    <div className="min-h-screen bg-gray-50" style={{ minHeight: 'calc(100vh - 7rem)' }}>
      <ListMetaTags list={list} />
      
      <div className="max-w-4xl mx-auto px-2 sm:px-4 py-2 sm:py-8 pt-3 sm:pt-20">
        {/* Back Button */}
        <div className="mb-2 sm:mb-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center px-3 py-1.5 sm:px-4 sm:py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 hover:text-gray-900 transition-colors shadow-sm text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
            {t('listDetail.backToDashboard')}
          </button>
        </div>

        {/* Header */}
        <div className="bg-white border border-gray-300 rounded-lg shadow-md p-3 sm:p-6 mb-2 sm:mb-6 mt-0 sm:mt-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 sm:mb-4 space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-2 sm:space-x-8">
              
              <div className="flex space-x-1 sm:space-x-2">
                {/* Share button - show for all lists */}
                <button
                  onClick={() => {
                    console.log('Share button clicked, opening modal');
                    setShowShareModal(true);
                  }}
                  className="flex items-center p-1.5 sm:p-2 text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                  title={t('listDetail.shareList')}
                >
                  <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
                
                {/* Edit and Delete buttons - only show for list owner */}
                {isOwner && (
                  <>
                    <button
                      onClick={() => navigate(`/edit-list/${list.id}`)}
                      className="flex items-center p-1.5 sm:p-2 text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                      title={t('listDetail.editList')}
                    >
                      <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="flex items-center p-1.5 sm:p-2 text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                      title={t('listDetail.deleteList')}
                    >
                      <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                  </>
                )}

                {/* Member list button - show for group lists */}
                {list.isGroup && list.members && list.members.length > 0 && (
                  <div className="relative" ref={memberDropdownRef}>
                    <button
                      onClick={() => setShowMemberList(!showMemberList)}
                      className="flex items-center p-1.5 sm:p-2 text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                      title={t('listDetail.members')}
                    >
                      <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                    
                    {showMemberList && (
                      <div className="absolute right-0 sm:right-0 left-0 sm:left-auto top-full mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                        <div className="p-3">
                          <div className="text-sm font-medium text-gray-900 mb-2">
                            {t('listDetail.members')} ({list.members.length})
                          </div>
                          <div className="max-h-48 overflow-y-auto space-y-1">
                            {list.members.map((memberId) => (
                              <div key={memberId} className="text-sm text-gray-600 py-1">
                                {memberNames[memberId] || t('common.loading')}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

          </div>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">{list.name}</h1>
              {list.description && (
                <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-4">{list.description}</p>
              )}
              
              <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                {list.occasion && (
                  <span className="flex items-center">
                    <Gift className="w-4 h-4 mr-1" />
                    {t(`createList.occasions.${list.occasion}`)}
                  </span>
                )}
                {list.eventDate && (
                  <span>
                    {(() => {
                      try {
                        // Handle both Firestore Timestamp and string dates
                        if (list.eventDate.toDate) {
                          return list.eventDate.toDate().toLocaleDateString();
                        } else if (typeof list.eventDate === 'string') {
                          return new Date(list.eventDate).toLocaleDateString();
                        } else {
                          return new Date(list.eventDate).toLocaleDateString();
                        }
                      } catch (error) {
                        console.error('Error formatting date:', error);
                        return list.eventDate.toString();
                      }
                    })()}
                  </span>
                )}
                {list.defaultCurrency && (
                  <span>{list.defaultCurrency}</span>
                )}
              </div>
            </div>

          </div>

          {/* Bottom Section - List Type and Stats */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
            {/* List Type Indicator - Bottom Left */}
            <div className="flex items-center space-x-2">
              {list.isGroup ? (
                <>
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-xs text-gray-500 font-medium">{t('dashboard.groupList')}</span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="text-xs text-gray-500 font-medium">{t('dashboard.personalList')}</span>
                </>
              )}
            </div>

            {/* Wish Stats - Bottom Right */}
            {wishCounts.total > 0 ? (
              <div className="flex items-center space-x-2">
                {/* Bought Stats */}
                <div className="flex items-center space-x-1.5">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  <span className="text-green-700 font-medium text-sm">
                    {wishCounts.bought}/{wishCounts.total} {t('dashboard.bought')}
                  </span>
                </div>
                {/* Planned Stats */}
                <div className="flex items-center space-x-1.5">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <span className="text-blue-700 font-medium text-sm">
                    {wishCounts.planned}/{wishCounts.total} {t('dashboard.planned')}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-1 text-gray-400">
                <Gift className="h-4 w-4" />
                <span className="text-sm">{t('dashboard.noWishesAddedYet')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Controls Section */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between">
          {/* Add Wish Button and Sort Controls - Side by side on mobile */}
          <div className="flex flex-row gap-2 sm:gap-4 w-full sm:w-auto justify-center sm:justify-start">
            {/* Add Wish Button */}
            {isMember && (
              <button
                onClick={() => setShowWishForm(true)}
                className="flex items-center px-6 py-2.5 sm:px-8 sm:py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm sm:text-base font-medium sm:flex-none"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" />
                {t('listDetail.addWish')}
              </button>
            )}

            {/* Grid Layout Controls */}
            {wishes.length > 0 && (
              <div className="relative hidden sm:block" ref={gridDropdownRef}>
                <button
                  onClick={() => setShowGridDropdown(!showGridDropdown)}
                  className="flex items-center justify-between px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-w-[11rem]"
                >
                  <div className="flex items-center min-w-0 mr-1.5">
                    {gridLayout === 'list' && <List className="w-4 h-4 mr-2 flex-shrink-0 text-gray-500" />}
                    {gridLayout === 'two-column' && <LayoutGrid className="w-4 h-4 mr-2 flex-shrink-0 text-gray-500" />}
                    {gridLayout === 'grid' && <Grid className="w-4 h-4 mr-2 flex-shrink-0 text-gray-500" />}
                    <span className="text-sm font-medium truncate">
                      {gridLayout === 'list' && t('listDetail.layoutList')}
                      {gridLayout === 'two-column' && t('listDetail.layoutTwoColumn')}
                      {gridLayout === 'grid' && t('listDetail.layoutGrid')}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 ml-1 flex-shrink-0 text-gray-400" />
                </button>
                
                {showGridDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <button
                      onClick={() => { setGridLayout('list'); setShowGridDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg flex items-center ${
                        gridLayout === 'list' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      <List className="w-4 h-4 mr-2" />
                      {t('listDetail.layoutList')}
                    </button>
                    <button
                      onClick={() => { setGridLayout('two-column'); setShowGridDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center ${
                        gridLayout === 'two-column' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      <LayoutGrid className="w-4 h-4 mr-2" />
                      {t('listDetail.layoutTwoColumn')}
                    </button>
                    <button
                      onClick={() => { setGridLayout('grid'); setShowGridDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 last:rounded-b-lg flex items-center ${
                        gridLayout === 'grid' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      <Grid className="w-4 h-4 mr-2" />
                      {t('listDetail.layoutGrid')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Wishes List */}
        {wishes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <Heart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('listDetail.noWishes')}</h3>
            <p className="text-gray-500">{t('listDetail.noWishesDescription')}</p>
          </div>
        ) : (
          (() => {
            const { myWishes, othersWishes } = separateWishes(wishes);
            const wishesContainerClass = `${gridLayout === 'list' ? 'space-y-4' : gridLayout === 'two-column' ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'}`;
            
            return (
              <div className="space-y-6">
                {/* My Wishes Section */}
                {myWishes.length > 0 && (
                  <div>
                    <button
                      onClick={() => setMyWishesCollapsed(!myWishesCollapsed)}
                      className="flex items-center justify-between w-full p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors mb-4"
                    >
                      <div className="flex items-center">
                        <User className="w-3.5 h-3.5 mr-2 text-blue-600" />
                        <h2 className="text-sm font-semibold text-blue-900">{t('listDetail.myWishes')} ({myWishes.length})</h2>
                      </div>
                      {myWishesCollapsed ? (
                        <ChevronDown className="w-3.5 h-3.5 text-blue-600" />
                      ) : (
                        <ChevronUp className="w-3.5 h-3.5 text-blue-600" />
                      )}
                    </button>
                    
                    {!myWishesCollapsed && (
                      <div className={wishesContainerClass}>
                        {myWishes.map(renderWish)}
                      </div>
                    )}
                  </div>
                )}

                {/* Others' Wishes Section */}
                {othersWishes.length > 0 && (
                  <div>
                    <button
                      onClick={() => setOthersWishesCollapsed(!othersWishesCollapsed)}
                      className="flex items-center justify-between w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors mb-4"
                    >
                      <div className="flex items-center">
                        <Users className="w-3.5 h-3.5 mr-2 text-gray-600" />
                        <h2 className="text-sm font-semibold text-gray-900">{t('listDetail.othersWishes')} ({othersWishes.length})</h2>
                      </div>
                      {othersWishesCollapsed ? (
                        <ChevronDown className="w-3.5 h-3.5 text-gray-600" />
                      ) : (
                        <ChevronUp className="w-3.5 h-3.5 text-gray-600" />
                      )}
                    </button>
                    
                    {!othersWishesCollapsed && (() => {
                      const groupedWishes = groupWishesByUser(othersWishes);
                      const userIds = Object.keys(groupedWishes).sort((a, b) => {
                        const nameA = memberNames[a] || t('common.unknownUser');
                        const nameB = memberNames[b] || t('common.unknownUser');
                        return nameA.localeCompare(nameB);
                      });
                      
                      return (
                        <div className="space-y-4">
                          {userIds.map(userId => {
                            const userWishes = groupedWishes[userId];
                            const userName = memberNames[userId] || t('common.unknownUser');
                            const isCollapsed = collapsedUsers.has(userId);
                            
                            // Calculate status counts for this user's wishes (only visible to non-creators for privacy)
                            const plannedCount = userWishes.filter(wish => wish.createdBy !== user.uid && wish.plannedBy && wish.plannedBy.length > 0).length;
                            const boughtCount = userWishes.filter(wish => wish.createdBy !== user.uid && wish.boughtBy && wish.boughtBy.length > 0).length;
                            
                            return (
                              <div key={userId} className="border border-gray-200 rounded-lg p-3 bg-white">
                                <button
                                  onClick={() => toggleUserCollapse(userId)}
                                  className="flex items-center justify-between w-full p-2 mb-2 hover:bg-gray-50 rounded transition-colors"
                                >
                                  <div className="flex items-center flex-1 min-w-0">
                                    <User className="w-3.5 h-3.5 mr-2 text-gray-600 flex-shrink-0" />
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <h3 className="text-sm font-semibold text-gray-900 truncate">{userName} ({userWishes.length})</h3>
                                      {/* Status indicators */}
                                      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                        {boughtCount > 0 && (
                                          <div className="flex items-center space-x-1">
                                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                            <span className="text-xs text-green-700 font-medium">{boughtCount}</span>
                                          </div>
                                        )}
                                        {plannedCount > 0 && (
                                          <div className="flex items-center space-x-1">
                                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                            <span className="text-xs text-blue-700 font-medium">{plannedCount}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {isCollapsed ? (
                                    <ChevronDown className="w-3.5 h-3.5 text-gray-600 ml-2 flex-shrink-0" />
                                  ) : (
                                    <ChevronUp className="w-3.5 h-3.5 text-gray-600 ml-2 flex-shrink-0" />
                                  )}
                                </button>
                                
                                {!isCollapsed && (
                                  <div className={wishesContainerClass}>
                                    {userWishes.map(renderWish)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })()
        )}
      </div>

      {/* Wish Form Modal */}
      {showWishForm && (
        <WishForm
          onSubmit={handleAddWish}
          onCancel={() => setShowWishForm(false)}
          defaultCurrency={list.defaultCurrency}
        />
      )}

      {/* Edit Wish Modal */}
      {editingWish && (
        <WishForm
          wish={editingWish}
          onSubmit={(wishData) => handleUpdateWish(editingWish.id, wishData)}
          onCancel={() => setEditingWish(null)}
          defaultCurrency={list.defaultCurrency}
        />
      )}

      {/* Share List Modal */}
      {showShareModal && list && (
        <ShareListModal
          isOpen={showShareModal}
          list={list}
          onClose={handleShareModalClose}
        />
      )}

      {/* Delete List Confirmation Modal */}
      {showDeleteModal && (
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteList}
          title={t('listDetail.deleteList')}
          message={t('listDetail.deleteListConfirm')}
          confirmText={t('listDetail.delete')}
          cancelText={t('listDetail.cancel')}
        />
      )}
    </div>
  );
};

export default ListDetail;
